import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpdeskRecipesAPI } from "../../../../api/helpdeskRecipes";
import ActionListEditor from "../ActionListEditor";

const EMPTY_RECIPE = {
  name: "",
  description: "",
  scope_type: "global",
  scope_id: null,
  department_ids: [],
  role_ids: [],
  actions: [],  
};

export default function RecipeEditor({ mode, recipeId }) {
  const navigate = useNavigate();

  const [recipe, setRecipe] = useState(EMPTY_RECIPE);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [notifications, setNotifications] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadEditorOptions();
  }, []);

  useEffect(() => {
    if (mode === "edit") {
      loadRecipe();
    }
  }, [mode, recipeId]);

  async function loadEditorOptions() {
    const load = async (request, setter, key) => {
      try {
        if (typeof request !== "function") {
          console.error(`Recipe editor API method missing for: ${key}`);
          setter([]);
          return;
        }

        const data = await request();
        setter(data?.[key] || []);
      } catch (err) {
        console.error(`Failed to load recipe editor option: ${key}`, err);
        setter([]);
      }
    };

    await Promise.all([
      load(
        HelpdeskRecipesAPI.listNotifications,
        setNotifications,
        "notifications"
      ),
      load(
        HelpdeskRecipesAPI.listSchoolOptions,
        setSchoolOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listDepartmentOptions,
        setDepartmentOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listRoleOptions,
        setRoleOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listStatusOptions,
        setStatusOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listCategoryOptions,
        setCategoryOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listSubcategoryOptions,
        setSubcategoryOptions,
        "options"
      ),
      load(
        HelpdeskRecipesAPI.listUsers,
        setUsers,
        "users"
      ),
    ]);
  }

  async function loadRecipe() {
    try {
      setError("");

      const data = await HelpdeskRecipesAPI.get(recipeId);

      setRecipe({
        ...EMPTY_RECIPE,
        ...data,
        department_ids: Array.isArray(data.department_ids)
          ? data.department_ids
          : [],
        role_ids: Array.isArray(data.role_ids)
          ? data.role_ids
          : [],
        actions: Array.isArray(data.actions)
          ? data.actions
          : [],
      });
    } catch (err) {
      console.error("Failed to load recipe", err);
      setError("Failed to load recipe.");
    } finally {
      setLoading(false);
    }
  }

  function toggleDepartment(departmentId) {
    const current = recipe.department_ids || [];

    const exists = current.some(
      (id) => String(id) === String(departmentId)
    );

    const updated = exists
      ? current.filter(
          (id) => String(id) !== String(departmentId)
        )
      : [...current, departmentId];

    setRecipe({
      ...recipe,
      department_ids: updated,
    });
  }

  function toggleRole(roleId) {
    const current = recipe.role_ids || [];

    const exists = current.some(
      (id) => String(id) === String(roleId)
    );

    const updated = exists
      ? current.filter(
          (id) => String(id) !== String(roleId)
        )
      : [...current, Number(roleId)];

    setRecipe({
      ...recipe,
      role_ids: updated,
    });
  }

  function validateRecipe() {
    if (!recipe.name?.trim()) {
      return "Recipe name is required.";
    }

    if (
      recipe.scope_type === "school" &&
      !recipe.scope_id
    ) {
      return "Select a school for this recipe.";
    }

    if (!Array.isArray(recipe.actions)) {
      return "Recipe actions must be an array.";
    }

    return "";
  }

  async function save() {
    const validationError = validateRecipe();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        name: recipe.name.trim(),
        description: recipe.description?.trim() || null,
        scope_type: recipe.scope_type,
        scope_id:
          recipe.scope_type === "school"
            ? Number(recipe.scope_id)
            : null,
        department_ids: recipe.department_ids || [],
        role_ids: (recipe.role_ids || []).map(Number),
        actions: (recipe.actions || []).map(
          (action, index) => ({
            ...action,
            action_order: index,
          })
        ),
      };

      if (mode === "create") {
        await HelpdeskRecipesAPI.create(payload);
      } else {
        await HelpdeskRecipesAPI.update(recipeId, payload);
      }

      navigate("/helpdesk/automation/recipes");
    } catch (err) {
      console.error("Failed to save recipe", err);
      setError(
        err?.message ||
          "Failed to save recipe. Check the recipe and try again."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div>Loading recipe…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">
          {mode === "create" ? "New recipe" : "Edit recipe"}
        </h2>

        <p className="text-sm text-gray-600 mt-1">
          Define a sequence of actions that technicians can run
          manually against a ticket.
        </p>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}

      <div className="border rounded p-5 space-y-4">
        <h3 className="font-semibold">Recipe details</h3>

        <div>
          <label className="block text-sm font-medium mb-1">
            Name
          </label>

          <input
            className="w-full border rounded p-2"
            value={recipe.name || ""}
            onChange={(e) =>
              setRecipe({
                ...recipe,
                name: e.target.value,
              })
            }
            placeholder="e.g. Email release"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Description
          </label>

          <textarea
            className="w-full border rounded p-2"
            value={recipe.description || ""}
            onChange={(e) =>
              setRecipe({
                ...recipe,
                description: e.target.value,
              })
            }
            placeholder="Describe when technicians should use this recipe."
          />
        </div>
      </div>

      <div className="border rounded p-5 space-y-4">
        <div>
          <h3 className="font-semibold">Ticket scope</h3>

          <p className="text-sm text-gray-600 mt-1">
            Choose which school's tickets this recipe can be used
            against.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Scope
          </label>

          <select
            className="border rounded p-2 w-full"
            value={recipe.scope_type}
            onChange={(e) =>
              setRecipe({
                ...recipe,
                scope_type: e.target.value,
                scope_id:
                  e.target.value === "global"
                    ? null
                    : recipe.scope_id,
              })
            }
          >
            <option value="global">Global (all schools)</option>
            <option value="school">Single school</option>
          </select>
        </div>

        {recipe.scope_type === "school" && (
          <div>
            <label className="block text-sm font-medium mb-1">
              School
            </label>

            <select
              className="border rounded p-2 w-full"
              value={recipe.scope_id || ""}
              onChange={(e) =>
                setRecipe({
                  ...recipe,
                  scope_id: e.target.value
                    ? Number(e.target.value)
                    : null,
                })
              }
            >
              <option value="">Select school</option>

              {schoolOptions.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="border rounded p-5 space-y-5">
        <div>
          <h3 className="font-semibold">Audience</h3>

          <p className="text-sm text-gray-600 mt-1">
            Control which technicians can see and run this recipe.
            Leave departments or roles empty to allow all values in
            that group.
          </p>
        </div>

        <div>
          <div className="font-medium text-sm mb-2">
            Departments
          </div>

          {departmentOptions.length === 0 ? (
            <div className="text-sm text-gray-500">
              No departments available.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {departmentOptions.map((department) => {
                const checked = (recipe.department_ids || []).some(
                  (id) =>
                    String(id) === String(department.id)
                );

                return (
                  <label
                    key={department.id}
                    className="flex items-center gap-2 border rounded p-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        toggleDepartment(department.id)
                      }
                    />

                    <span className="text-sm">
                      {department.name}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="text-xs text-gray-500 mt-2">
            None selected = all departments.
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="font-medium text-sm mb-2">
            Roles
          </div>

          {roleOptions.length === 0 ? (
            <div className="text-sm text-gray-500">
              No roles available.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {roleOptions.map((role) => {
                const checked = (recipe.role_ids || []).some(
                  (id) => String(id) === String(role.id)
                );

                return (
                  <label
                    key={role.id}
                    className="flex items-center gap-2 border rounded p-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(role.id)}
                    />

                    <span className="text-sm">
                      {role.name}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div className="text-xs text-gray-500 mt-2">
            None selected = all roles.
          </div>
        </div>
      </div>

      <div className="border rounded p-5 space-y-4">
        <div>
          <h3 className="font-semibold">Actions</h3>

          <p className="text-sm text-gray-600 mt-1">
            Actions run in order from top to bottom when a
            technician runs the recipe.
          </p>
        </div>

        <ActionListEditor
          actions={recipe.actions || []}
          onChange={(actions) =>
            setRecipe({
              ...recipe,
              actions,
            })
          }
          notifications={notifications}
          statusOptions={statusOptions}
          categoryOptions={categoryOptions}
          subcategoryOptions={subcategoryOptions}
          users={users}
        />
      </div>

      <div className="border rounded p-5">
        <details>
          <summary className="cursor-pointer font-medium">
            Recipe preview
          </summary>

          <pre className="bg-gray-100 p-4 text-xs overflow-auto rounded border mt-3">
            {JSON.stringify(recipe, null, 2)}
          </pre>
        </details>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save recipe"}
        </button>

        <button
          type="button"
          onClick={() =>
            navigate("/helpdesk/automation/recipes")
          }
          disabled={saving}
          className="px-4 py-2 border rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}